import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Instagram, Youtube, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CONTACT, SERVICES, waLink } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { icon: MessageCircle, label: "WhatsApp", value: CONTACT.whatsappDisplay, href: CONTACT.whatsapp, testid: "contact-whatsapp" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
  { icon: Instagram, label: "Instagram", value: CONTACT.instagramHandle, href: CONTACT.instagram, testid: "contact-instagram" },
  { icon: Youtube, label: "YouTube", value: "Meditation Library", href: CONTACT.youtube, testid: "contact-youtube" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your message has been received. We'll be in touch soon.");
      setForm({ name: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or email instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-pad py-24 md:py-36">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Booking / channels */}
        <div className="lg:col-span-5">
          <Overline>Book a Session</Overline>
          <Reveal>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-6 mb-7">
              Begin your journey inward.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-sans-ui text-[var(--muted-ink)] text-base leading-relaxed font-light max-w-md mb-9">
              Reach out via WhatsApp or email to book your session. All sessions are conducted online, so you can connect from any part of the world. Available 9 AM &ndash; 5 PM IST.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <a
              href={waLink("Namaste! I'd like to book a session with Brahmsara.")}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-book-btn"
              className="inline-flex items-center gap-2 font-sans-ui text-sm tracking-wide px-9 py-4 bg-[var(--gold)] text-[var(--noir)] rounded-full hover:bg-[var(--gold)] transition-colors duration-400 mb-12"
            >
              <MessageCircle size={17} /> Book on WhatsApp
            </a>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-px bg-[rgba(200,149,54,0.25)] border border-[rgba(200,149,54,0.25)]">
            {CHANNELS.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.label}
                  href={c.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={c.testid}
                  className="group bg-[var(--ivory)] p-6 hover:bg-[var(--noir-2)] transition-colors duration-400"
                >
                  <div className="flex items-center justify-between mb-4">
                    <Icon size={22} strokeWidth={1.4} className="text-[var(--gold)]" />
                    <ArrowUpRight size={16} className="text-[var(--muted-ink)] group-hover:text-[var(--ink)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300" />
                  </div>
                  <p className="font-sans-ui text-xs tracking-widest uppercase text-[var(--muted-ink)]">{c.label}</p>
                  <p className="font-serif-display text-lg text-[var(--ink)] mt-0.5">{c.value}</p>
                </a>
              );
            })}
          </div>
        </div>

        {/* Enquiry form */}
        <div className="lg:col-span-7">
          <Reveal>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-[var(--alt)] border border-[rgba(200,149,54,0.3)] p-8 md:p-12"
            >
              <h3 className="font-serif-display text-3xl md:text-4xl mb-2">Send a message</h3>
              <p className="font-sans-ui text-sm text-[var(--muted-ink)] font-light mb-9">
                Prefer to write first? Share a little about what brings you here.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="name" className="font-sans-ui text-xs tracking-widest uppercase text-[var(--muted-ink)]">Name</Label>
                  <Input id="name" data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className="bg-[var(--panel)] border-[rgba(200,149,54,0.4)] h-12 rounded-none focus-visible:ring-[var(--gold)]" />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email" className="font-sans-ui text-xs tracking-widest uppercase text-[var(--muted-ink)]">Email</Label>
                  <Input id="email" type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-[var(--panel)] border-[rgba(200,149,54,0.4)] h-12 rounded-none focus-visible:ring-[var(--gold)]" />
                </div>
              </div>

              <div className="flex flex-col gap-2 mt-6">
                <Label className="font-sans-ui text-xs tracking-widest uppercase text-[var(--muted-ink)]">Practice of interest</Label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger data-testid="contact-service-select" className="bg-[var(--panel)] border-[rgba(200,149,54,0.4)] h-12 rounded-none focus:ring-[var(--gold)]">
                    <SelectValue placeholder="Select a practice (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.title} value={s.title} data-testid={`service-option-${s.title}`}>{s.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2 mt-6">
                <Label htmlFor="message" className="font-sans-ui text-xs tracking-widest uppercase text-[var(--muted-ink)]">Message</Label>
                <Textarea id="message" data-testid="contact-message-input" value={form.message} onChange={set("message")} rows={5} placeholder="What brings you to Brahmsara?" className="bg-[var(--panel)] border-[rgba(200,149,54,0.4)] rounded-none resize-none focus-visible:ring-[var(--gold)]" />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="mt-8 font-sans-ui text-sm tracking-wide px-9 py-4 bg-[var(--gold)] text-[var(--noir)] rounded-full hover:bg-[var(--gold)] transition-colors duration-400 disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
